import { z } from 'zod';

/**
 * Ask Buakei over the Acts (ADR-017). An answer is a list of text segments; each segment
 * lists the sources it cites. Every source resolves to a known source, version and passage
 * held in the library, with the exact quoted text and the pages of the original document.
 */

export const ASK_QUESTION_MAX = 1000;

/** Evaluation accounts only (ADR-022): run on a named model route instead of the routed one. */
export const EvaluationRouteSchema = z.enum(['routine', 'analysis']).optional();

export const AskRequestSchema = z.object({
  question: z.string().trim().min(3).max(ASK_QUESTION_MAX),
  /** Continue this chat (ADR-019); omitted starts a new one. */
  chatId: z
    .string()
    .regex(/^chat_[A-Za-z0-9_-]{8,64}$/)
    .optional(),
  /** Research depth (ADR-022); "quick" may use the routine model once it is evaluated. */
  mode: z.enum(['quick', 'standard']).optional(),
  evaluationRoute: EvaluationRouteSchema,
  /** Research documents to read for this question (ADR-045): the caller's own, in one workspace. */
  documents: z
    .object({
      workspaceId: z.string().min(1).max(128),
      ids: z
        .array(z.string().regex(/^rdoc_[A-Za-z0-9]{8,64}$/))
        .min(1)
        .max(5),
    })
    .strict()
    .optional(),
});
export type AskRequest = z.infer<typeof AskRequestSchema>;

/**
 * How an external (non-Sierra Leone) source may be used (ADR-038). Assigned by the server
 * from the source's domain and address, never by the model. None of these is binding
 * Sierra Leone authority.
 */
export const ExternalAuthoritySchema = z.enum([
  'persuasive_english',
  'privy_council',
  'persuasive_commonwealth',
  'received_law_established',
  'received_law_unconfirmed',
  'secondary_commentary',
  'status_uncertain',
]);
export type ExternalAuthority = z.infer<typeof ExternalAuthoritySchema>;

export const EXTERNAL_AUTHORITY_LABEL: Record<ExternalAuthority, string> = {
  persuasive_english: 'Persuasive English authority',
  privy_council: 'Privy Council authority: persuasive unless its binding effect is established',
  persuasive_commonwealth: 'Persuasive Commonwealth authority',
  received_law_established: 'Received law: applicability established',
  received_law_unconfirmed: 'Received law: applicability requires further confirmation',
  secondary_commentary: 'Secondary commentary',
  status_uncertain: 'Status uncertain',
};

export const ExternalSourceSchema = z.object({
  url: z.string(),
  domain: z.string(),
  accessedAt: z.string(),
  /**
   * 1 primary legal source, 2 strong secondary source, 3 educational source: cited only when
   * the primary judgment it names could not be read, and then labelled as such.
   */
  tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  primary: z.boolean(),
  jurisdiction: z.string(),
  court: z.string().nullable(),
  citation: z.string().nullable(),
  authority: ExternalAuthoritySchema,
  /** "full": the page was read whole; "partial": only its first part fitted. */
  inspected: z.enum(['full', 'partial']),
  /** Educational sources: the primary judgment they name, which could not be read, and why. */
  primaryNotInspected: z
    .object({ citation: z.string().nullable(), url: z.string().nullable(), reason: z.string() })
    .optional(),
});
export type ExternalSource = z.infer<typeof ExternalSourceSchema>;

export const AskSourceSchema = z.object({
  n: z.number().int().positive(),
  /**
   * An Act or a judgment in the library (ADR-018), or an external source found by common-law
   * research (ADR-038); decides where "open the original" leads.
   */
  collection: z.enum(['acts', 'cases', 'external']).default('acts'),
  sourceId: z.string(),
  versionId: z.string(),
  passageId: z.string(),
  title: z.string(),
  year: z.number().int().nullable(),
  label: z.string(),
  pageStart: z.number().int(),
  pageEnd: z.number().int(),
  /** Exact text the answer relies on, quoted from the extracted passage. */
  quotes: z.array(z.string()),
  /** External sources only: where it came from and how it may be used. */
  external: ExternalSourceSchema.optional(),
});
export type AskSource = z.infer<typeof AskSourceSchema>;

/** What external common-law research did for an answer (ADR-038), shown to the reader. */
export const ExternalResearchLogSchema = z.object({
  issue: z.string(),
  query: z.string(),
  at: z.string(),
  events: z.array(
    z.object({
      kind: z.enum(['searched', 'opened', 'discovery', 'not_found', 'refused', 'failed']),
      url: z.string().nullable(),
      title: z.string().nullable(),
      note: z.string(),
    }),
  ),
});
export type ExternalResearchLog = z.infer<typeof ExternalResearchLogSchema>;

export const AskAnswerSchema = z.object({
  /** `not_found` means nothing relevant WITHIN THE ACTS SEARCHED, not that no law exists. */
  outcome: z.enum(['answered', 'not_found', 'declined']),
  segments: z.array(z.object({ text: z.string(), sources: z.array(z.number().int()) })),
  sources: z.array(AskSourceSchema),
  searched: z.object({ acts: z.number().int(), passages: z.number().int() }),
  remainingToday: z.number().int(),
  model: z.string(),
  /** External common-law research run for this answer (ADR-038), if any. */
  externalResearch: z.array(ExternalResearchLogSchema).optional(),
  /** The chat this answer belongs to (ADR-019); absent when it could not be saved. */
  chatId: z.string().optional(),
  /**
   * Set when lawyer-approved key points for a matching question guided the research (ADR-035).
   * The answer itself has not been reviewed by a lawyer.
   */
  guidedBy: z.object({ keyPointsId: z.string(), approvedAt: z.string() }).optional(),
  /**
   * The user's documents read for this answer (ADR-044, ADR-045), with the pages read. They are
   * the user's own material, never legal authority, and are not numbered sources.
   */
  documentsRead: z.array(z.object({ name: z.string(), pages: z.string() })).optional(),
});
export type AskAnswer = z.infer<typeof AskAnswerSchema>;

/** Chat history (ADR-019): private to the account that asked. */
export const CHAT_TURN_LIMIT = 20;

export const ChatTurnSchema = z.object({
  question: z.string(),
  answer: AskAnswerSchema,
  askedAt: z.string(),
});
export type ChatTurn = z.infer<typeof ChatTurnSchema>;

export const ChatSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  updatedAt: z.string(),
  turns: z.number().int(),
});
export type ChatSummary = z.infer<typeof ChatSummarySchema>;

export const ChatSchema = ChatSummarySchema.extend({
  createdAt: z.string(),
  history: z.array(ChatTurnSchema),
});
export type Chat = z.infer<typeof ChatSchema>;

export const ChatListSchema = z.object({ chats: z.array(ChatSummarySchema) });
export type ChatList = z.infer<typeof ChatListSchema>;

/** A case brief (ADR-020): a structured, cited summary of one judgment, shared by all readers. */
export const CaseBriefSchema = AskAnswerSchema.extend({
  versionId: z.string(),
  caseTitle: z.string(),
  /** True when the saved brief was served (no question used). */
  cached: z.boolean(),
  createdAt: z.string(),
});
export type CaseBrief = z.infer<typeof CaseBriefSchema>;

/** Live quizzes (ADR-021): multiple-choice questions written from passages of the library. */
export const QuizRequestSchema = z.object({
  topic: z.string().trim().min(3).max(200),
  evaluationRoute: EvaluationRouteSchema,
});

export const LiveQuizQuestionSchema = z.object({
  id: z.string(),
  prompt: z.string(),
  options: z.array(z.object({ id: z.string(), text: z.string() })).min(2),
  correctOptionId: z.string(),
  explanation: z.string(),
  /** Numbers of the quiz's sources that support the answer. */
  sources: z.array(z.number().int()),
});
export type LiveQuizQuestion = z.infer<typeof LiveQuizQuestionSchema>;

export const LiveQuizSchema = z.object({
  outcome: z.enum(['ready', 'not_found', 'declined']),
  topic: z.string(),
  title: z.string(),
  questions: z.array(LiveQuizQuestionSchema),
  sources: z.array(AskSourceSchema),
  remainingToday: z.number().int(),
  model: z.string(),
});
export type LiveQuiz = z.infer<typeof LiveQuizSchema>;
